<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex inline-flex items-center text-sm text-gray-500"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Pedal Me Integration" />
            </div>

            <BaseSpinner :isLoading="isLoading" />
        </div>
        <div v-if="!isLoading && !showSettings" class="py-8">
            <FeaturePanelWithScreenshot
                title="Pedal me london courier"
                subtitle=""
                description="Sell direct for delivery, without having your own drivers."
                :features="pedalMeFeatures"
                image="https://ucarecdn.com/44ba65b5-1476-4e9a-bc26-f2daf69bba7b/-/quality/smart_retina/-/format/auto/"
            >
                <template #cta>
                    <base-button
                        v-if="
                            isFeatureAvailable(FeaturesEnum.SAAS) ||
                            isFeatureAvailable(FeaturesEnum.INTERNAL)
                        "
                        class="mt-2 sm:ml-3 sm:mt-0"
                        buttonText="Enable Pedal Me"
                        @clicked="showSettings = true"
                    />
                    <base-upsell-button
                        v-else
                        upsell-item="pedalme"
                        buttonText="Upgrade to enable Pedal Me"
                        :disabled="isLoading"
                    ></base-upsell-button>
                </template>
            </FeaturePanelWithScreenshot>
        </div>
        <div v-if="!isLoading && showSettings">
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                            >Details</h3
                        >
                        <p class="mt-2 text-sm text-gray-500">
                            Enter your Pedal Me credentials to integrate with
                            their courier service.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <div
                                v-if="pedalme.token"
                                class="mb-4 rounded-md bg-green-50 p-4"
                            >
                                <div class="flex">
                                    <div class="flex-shrink-0">
                                        <svg
                                            class="h-5 w-5 text-green-400"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clip-rule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <p
                                            class="text-sm font-medium text-green-800"
                                        >
                                            Connected to Pedal Me
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3 sm:col-span-2">
                                    <base-input-field
                                        label="Email"
                                        id="pedalMeEmail"
                                        v-model="pedalme.email"
                                        :errors="$v.pedalme.email.$error"
                                        placeholder="e.g. restaurant@example.com"
                                    />
                                    <base-password-input
                                        v-model="pedalme.password"
                                        inputId="pedalmePassword"
                                        class="mt-4"
                                        :error="$v.pedalme.password.$error"
                                    />

                                    <div class="mt-4">
                                        <base-button
                                            buttonText="Connect to Pedal Me"
                                            @clicked="connectToPedalMe"
                                            :disabled="isLoading"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="grid grid-cols-3 gap-6">
                                <div class="sm:col-span-6">
                                    <label
                                        for="orderStatus"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                    >
                                        Order status
                                    </label>
                                    <select
                                        id="orderStatus"
                                        name="orderStatus"
                                        v-model.trim="
                                            pedalme.orderStatusTrigger
                                        "
                                        class="form-input mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                                    >
                                        <option
                                            v-for="orderStatus in orderStatuses"
                                            :key="orderStatus"
                                            :value="orderStatus"
                                        >
                                            {{ orderStatus }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-span-3">
                                    <label
                                        for="enabled"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                    >
                                        Integration Status
                                    </label>

                                    <div class="mt-2 flex items-start">
                                        <toggle-switch
                                            v-model="pedalme.enabled"
                                            :is-disabled="!hasValidConnection"
                                        />

                                        <p class="pl-4 text-sm text-gray-500">
                                            {{
                                                pedalme.enabled
                                                    ? 'Enabled - sending orders to Pedal Me'
                                                    : 'Disabled - not sending orders to Pedal Me'
                                            }}
                                        </p>
                                    </div>
                                    <div
                                        v-if="!hasValidConnection"
                                        class="mt-2 text-xs text-red-600"
                                    >
                                        Please connect to Pedal Me first before
                                        enabling the integration.
                                    </div>
                                </div>
                            </div>

                            <toggle-switch
                                label="Enable Return Job"
                                v-model="pedalme.enableReturnJob"
                                @input="clearNotes"
                            />

                            <div v-if="pedalme.enableReturnJob">
                                <label
                                    for="returnJobNotes"
                                    class="text-sm font-medium leading-5 text-gray-700"
                                >
                                    Return Job Notes
                                    <span class="text-xs text-gray-400"
                                        >(Optional)</span
                                    >
                                </label>
                                <base-text-area
                                    v-model.trim="pedalme.returnJobNotes"
                                    :rows="3"
                                    id="returnJobNotes"
                                    class="w-full"
                                    :maxLength="200"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-8 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                            >Service Size Brackets</h3
                        >
                        <p class="mt-2 text-sm text-gray-500">
                            Define custom cargo size brackets based on order subtotal. Orders will be assigned the appropriate vehicle size based on these rules.
                        </p>
                        <p class="mt-2 text-xs text-gray-400">
                            Available sizes: cargoMicro, cargoTiny, cargoSmall, cargo
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <rule-builder
                                v-model="pedalme.serviceSizeBrackets"
                                :fields="serviceSizeBracketFieldsWithCurrency"
                                label="Service Size Rules"
                                description="Define the order subtotal ranges for each cargo size."
                                addButtonText="Add Size Bracket"
                                emptyMessage="No size brackets defined. Default logic will be used."
                                :defaultRule="defaultServiceSizeBracket"
                                :validate="validateServiceSizeBracket"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BaseSpacerWithRuler />

        <div class="my-6 ml-4 flex flex-shrink-0 justify-end">
            <span class="inline-flex rounded-md shadow-sm" @click="saveVenue">
                <button
                    type="button"
                    class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none active:bg-indigo-700"
                    :disabled="isLoading"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import { FeaturesEnum, StatusEnum } from '@/enums';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch.vue';
import FeaturePanelWithScreenshot from '@/components/widgets/FeaturePanelWithScreenshot.vue';
import RuleBuilder from '@/components/formComponents/RuleBuilder.vue';

export default {
    name: 'PedalMeSettings',
    components: { FeaturePanelWithScreenshot, ToggleSwitch, RuleBuilder },
    metaInfo: {
        title: 'Pedal Me Integration'
    },
    props: {
        venueId: {
            type: Number,
            required: true
        },
        venue: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            FeaturesEnum,
            isLoading: false,
            showSettings: false,
            pedalme: {
                email: '',
                password: '',
                enabled: false,
                orderStatusTrigger: 'Accepted',
                enableReturnJob: false,
                returnJobNotes: '',
                token: null,
                serviceSizeBrackets: []
            },
            orderStatuses: [
                StatusEnum.ACCEPTED,
                StatusEnum.PREPARING,
                StatusEnum.OUT_FOR_DELIVERY
            ],
            pedalMeFeatures: [
                {
                    name: 'No commission fees',
                    description:
                        'Tap into the best fleets of delivery drivers, reducing costs of third-party delivery by up to 75%.'
                },
                {
                    name: 'Pick your vehicles',
                    description:
                        'Create your own rules for how vehicle types are offered delivery jobs - like cars for orders over £100!'
                },
                {
                    name: 'End-to-end tracking',
                    description:
                        'Keep your customers (and your teams) in the know with order tracking, from acceptance to delivery.'
                }
            ],
            serviceSizeBracketFields: [
                {
                    key: 'minAmount',
                    label: 'Min Amount',
                    type: 'currency',
                    placeholder: '0.00',
                    required: true
                },
                {
                    key: 'maxAmount',
                    label: 'Max Amount',
                    type: 'currency',
                    placeholder: 'Leave empty for no limit',
                    required: false
                },
                {
                    key: 'serviceSize',
                    label: 'Service Size',
                    type: 'select',
                    required: true,
                    options: [
                        { value: 'cargoMicro', label: 'Cargo Micro' },
                        { value: 'cargoTiny', label: 'Cargo Tiny' },
                        { value: 'cargoSmall', label: 'Cargo Small' },
                        { value: 'cargo', label: 'Cargo' }
                    ]
                }
            ],
            defaultServiceSizeBracket: {
                minAmount: 0,
                maxAmount: null,
                serviceSize: 'cargoSmall'
            }
        };
    },
    validations: {
        pedalme: {
            email: { required },
            password: { required }
        }
    },
    async created() {
        await this.fetchPedalMeSettings();
    },
    computed: {
        ...mapGetters({
            isFeatureAvailable: 'user/isFeatureAvailable'
        }),
        hasValidConnection() {
            return !!this.pedalme.token;
        },
        currencySymbol() {
            // Get currency symbol from venue, default to £
            const currencyMap = {
                'GBP': '£',
                'USD': '$',
                'EUR': '€'
            };
            return currencyMap[this.venue?.currencyCode] || '£';
        },
        serviceSizeBracketFieldsWithCurrency() {
            // Add currency symbol to currency fields
            return this.serviceSizeBracketFields.map(field => {
                if (field.type === 'currency') {
                    return {
                        ...field,
                        currencySymbol: this.currencySymbol
                    };
                }
                return field;
            });
        }
    },
    methods: {
        async fetchPedalMeSettings() {
            try {
                this.isLoading = true;
                const { data } = await this.$axios.get(
                    `/venues/${this.venueId}/settings/pedalme`
                );

                if (data) {
                    this.pedalme = {
                        ...this.pedalme,
                        ...data,
                        email: '',
                        password: '',
                        orderStatusTrigger:
                            data.orderStatusTrigger || 'Accepted'
                    };
                    this.showSettings = true;
                }
            } catch (e) {
                throw new Error(e);
            } finally {
                this.isLoading = false;
            }
        },
        clearNotes() {
            if (!this.pedalme.enableReturnJob) {
                this.pedalme.returnJobNotes = '';
            }
        },
        async saveVenue() {
            try {
                this.isLoading = true;

                const payload = {
                    venueId: this.venueId,
                    enabled: this.pedalme.enabled,
                    orderStatusTrigger: this.pedalme.orderStatusTrigger,
                    enableReturnJob: this.pedalme.enableReturnJob,
                    returnJobNotes: this.pedalme.returnJobNotes,
                    serviceSizeBrackets: this.pedalme.serviceSizeBrackets || []
                };

                await this.$axios.post('/pedalme/update', payload);

                this.$notify({
                    group: 'settings',
                    title: 'Settings saved'
                });
            } catch (e) {
                this.$notify({
                    group: 'settings',
                    title: 'Failed to save settings',
                    type: 'error'
                });
                throw new Error(`API ${e}`);
            } finally {
                this.isLoading = false;
            }
        },
        validateServiceSizeBracket(rule) {
            const errors = {};

            if (rule.minAmount === null || rule.minAmount === undefined || rule.minAmount < 0) {
                errors.minAmount = 'Min amount is required and must be >= 0';
            }

            if (rule.maxAmount !== null && rule.maxAmount !== undefined) {
                if (rule.maxAmount < 0) {
                    errors.maxAmount = 'Max amount must be >= 0';
                } else if (rule.maxAmount <= rule.minAmount) {
                    errors.maxAmount = 'Max amount must be greater than min amount';
                }
            }

            if (!rule.serviceSize) {
                errors.serviceSize = 'Service size is required';
            }

            return errors;
        },
        async connectToPedalMe() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return false;
            }

            try {
                this.isLoading = true;

                const payload = {
                    venueId: this.venueId,
                    email: this.pedalme.email,
                    password: this.pedalme.password
                };

                await this.$axios.post('/pedalme/auth', payload);

                this.pedalme.email = '';
                this.pedalme.password = '';

                await this.fetchPedalMeSettings();

                this.$notify({
                    group: 'settings',
                    title: 'Successfully connected to Pedal Me'
                });
            } catch {
                this.$notify({
                    group: 'settings',
                    title:
                        'Failed to connect to Pedal Me. Please check your credentials.',
                    type: 'error'
                });
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
